import { useNavigate } from '@tanstack/react-router';
import { array } from 'fp-ts';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { PortfolioRateGroupOptions, useRepositoryMutation, useRepositoryQuery } from '@mews-commander/data-access';
import { DataTestId } from '@mews-commander/enums';
import {
    SteppedDialogFormProps,
    SteppedPrimaryActions,
    SteppedPrimaryActionsProps,
    useSteppedDialogForm,
    useSteppedDialogFormControls,
} from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { RateGroupFormStep, WithEnterpriseIds } from '../../../../../types';
import { SelectedEnterpriseIdsContextProvider } from '../contexts/SelectedEnterpriseIdsContext';
import { RateGroupFormDetailsFields, RateGroupFormPaymentPoliciesFields } from '../fields';
import { useIntersectingServicesQuery } from '../queries/intersectingServices';
import {
    rateGroupDetailsSchema,
    RateGroupFormValues,
    rateGroupSchema,
    toPortfolioCentralRateGroupAddParameters,
} from '../schemas/rateGroupFormSchema';

const matchFormFields = (step: RateGroupFormStep) => {
    switch (step) {
        case 'details':
            return <RateGroupFormDetailsFields />;
        case 'paymentPolicies':
            return <RateGroupFormPaymentPoliciesFields />;
    }
};

interface AssignableEnterpriseIds extends WithEnterpriseIds {
    propertyGroupsIds?: string[];
}

export const useCreateRateGroupDialogForm = (assignableEnterpriseIds: AssignableEnterpriseIds) =>
    useSteppedDialogForm<RateGroupFormStep>({
        id: `createRateGroupForm`,
        title: lt('RateGroupCreateDialogTitle', {
            count: assignableEnterpriseIds.enterpriseIds.length,
        }),
        steps: ['details', 'paymentPolicies'],
        stepLabels: {
            details: lt('DetailPlural'),
            paymentPolicies: lt('GeneralPolicyPlural'),
        },
        renderForm: (props) => <CreateRateGroupFormContent {...props} {...assignableEnterpriseIds} />,
        renderPrimaryAction: (props) => (
            <CreateRateGroupFormPrimaryActions {...props} enterpriseIds={assignableEnterpriseIds.enterpriseIds} />
        ),
    });

const CreateRateGroupFormContent = ({
    enterpriseIds,
    propertyGroupsIds,
    ...props
}: SteppedDialogFormProps<RateGroupFormStep> & AssignableEnterpriseIds) => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    const navigate = useNavigate();

    const { mutateAsync } = useRepositoryMutation('portfolioCentralRateGroups', 'addNew', {
        invalidateQueries: {
            portfolioCentralRateGroups: ['getOverview'],
            portfolioRateGroups: ['getOverview'],
        },
    });

    const { renderFormContent } = useSteppedDialogFormControls<RateGroupFormStep, RateGroupFormValues>({
        ...props,
        defaultValues: {
            rateGroupType: 'central',
        },
        stepResolvers: {
            details: createZodFormResolver(rateGroupDetailsSchema),
            paymentPolicies: createZodFormResolver(rateGroupSchema),
        },
        onSubmit: async (formValues: RateGroupFormValues) => {
            const { requirePaymentCardDuringOnlineCheckIn, ...restFormValues } = formValues;
            if (requirePaymentCardDuringOnlineCheckIn) {
                restFormValues.options = restFormValues.options
                    ? [...restFormValues.options, PortfolioRateGroupOptions.RequirePaymentCardDuringOnlineCheckIn]
                    : [PortfolioRateGroupOptions.RequirePaymentCardDuringOnlineCheckIn];
            }
            const values = rateGroupSchema.transform(toPortfolioCentralRateGroupAddParameters).parse(restFormValues);
            await mutateAsync(
                {
                    ...values,
                    enterpriseIds: propertyGroupsIds ? null : enterpriseIds,
                    enterpriseGroupIds: propertyGroupsIds,
                },
                {
                    onSuccess: () => {
                        showSuccessAlert({
                            title: lt('CentralRateGroupCreateSuccessMessage', { rateGroupName: values.name }),
                            action: {
                                text: lt('RateGroupCreatedActionText'),
                                onClick: () => {
                                    void navigate({
                                        to: '/$currentEnterpriseId/EnterpriseRateManagement/RateGroups/Index',
                                        params: { currentEnterpriseId },
                                    });
                                },
                            },
                        });
                    },
                }
            );
        },
    });

    return renderFormContent((step) => (
        <SelectedEnterpriseIdsContextProvider value={{ enterpriseIds }}>
            {matchFormFields(step)}
        </SelectedEnterpriseIdsContextProvider>
    ));
};

const CreateRateGroupFormPrimaryActions = ({
    enterpriseIds,
    ...props
}: SteppedPrimaryActionsProps<RateGroupFormStep> & WithEnterpriseIds) => {
    const { isFetching, noIntersectingServices } = useIntersectingServicesQuery({ enterpriseIds });
    const { data, isFetching: isFetchingPaymentPolicies } = useRepositoryQuery(
        'portfolioCentralPaymentPolicies',
        'getAll',
        {}
    );
    const noPaymentPolicies = data ? array.isEmpty(data.centralPaymentPolicies) : true;

    return (
        <SteppedPrimaryActions
            {...props}
            disabled={isFetching || noIntersectingServices || isFetchingPaymentPolicies || noPaymentPolicies}
            nextStepButtonDataTestId={DataTestId.RateGroupFormNextButton}
            submitButtonDataTestId={DataTestId.RateGroupFormSubmitButton}
        />
    );
};
